import { z } from 'zod';
import { directoryProviderSchema } from './directory-provider.js';
import { MAX_PAGE_SIZE, pageQuerySchema } from './http.js';
import { e164Schema } from './phone.js';
import { freeText, label } from './text-policy.js';
/** Who the Directory lists (adr/0036 decision 6): employees and contractors, never visitors. */
export const DIRECTORY_PERSON_TYPES = ['EMPLOYEE', 'CONTRACTOR'];
export const directoryPersonTypeSchema = z.enum(DIRECTORY_PERSON_TYPES);
/** A department or location as a card shows it. */
export const directoryRefDTOSchema = z
    .object({ id: z.string().min(1), name: z.string().min(1) })
    .strict();
/**
 * Where a person's directory photo is served (S47-rsq5 Phase 4, adr/0036 addendum "Employee photos").
 * AN API-RELATIVE PATH, NOT A URL, and ONE PER SURFACE (orchestrator ruling 2026-10-18, agreed with S48):
 * web DTOs carry the `/api/web` path (fetched by the SPA through the SDK); `/api/mobile/v1` DTOs carry the
 * `/api/mobile/v1` path (Express prefixes its API origin). Both are served by the SAME service call with the
 * employee bearer token - no signed URL, no public object. Built ONLY by the server; it embeds the file id,
 * so a replaced photo is a new path and a client may cache by path. Clients treat it as opaque.
 */
export const DIRECTORY_WEB_PHOTO_PATH_PATTERN = /^\/api\/web\/directory\/people\/[^/?#]+\/photo\/[^/?#]+$/;
export const DIRECTORY_MOBILE_PHOTO_PATH_PATTERN = /^\/api\/mobile\/v1\/directory\/people\/[^/?#]+\/photo\/[^/?#]+$/;
export const directoryWebPhotoPathSchema = z.string().regex(DIRECTORY_WEB_PHOTO_PATH_PATTERN);
export const directoryMobilePhotoPathSchema = z.string().regex(DIRECTORY_MOBILE_PHOTO_PATH_PATTERN);
export function directoryWebPhotoPath(personId, fileId) {
    return `/api/web/directory/people/${encodeURIComponent(personId)}/photo/${encodeURIComponent(fileId)}`;
}
export function directoryMobilePhotoPath(personId, fileId) {
    return `/api/mobile/v1/directory/people/${encodeURIComponent(personId)}/photo/${encodeURIComponent(fileId)}`;
}
/** The one switch a mapper uses: `null` file id -> `null`; otherwise the surface's builder. */
export function directoryPhotoPathFor(surface, personId, fileId) {
    if (fileId === null)
        return null;
    return surface === 'web' ? directoryWebPhotoPath(personId, fileId) : directoryMobilePhotoPath(personId, fileId);
}
/**
 * `?size=` on both photo routes - a PLANNED SHAPE (orchestrator ruling 2026-10-18). Optional, default
 * `full`. In M1 both sizes return the stored 512 px image (no server image library); a ~128 px `thumb`
 * generated at upload is the planned follow-up, and neither the path nor this parameter will change.
 */
export const DIRECTORY_PHOTO_SIZES = ['thumb', 'full'];
export const directoryPhotoQuerySchema = z
    .object({ size: z.enum(DIRECTORY_PHOTO_SIZES).default('full') })
    .strict();
/**
 * One face in the grid. AN ALLOW-LIST (docs/008 S7): no email, phone, userId, externalId or
 * employment status — the profile (Phase 3) carries contact details. `photoPath` — the derived
 * serving path, never the storage key or raw file id (S7). `isSelf` is computed
 * server-side from the verified token, never sent by a client.
 */
export const directoryPersonSummaryDTOSchema = z
    .object({
    id: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    title: z.string().nullable(),
    personType: directoryPersonTypeSchema,
    department: directoryRefDTOSchema.nullable(),
    primaryLocation: directoryRefDTOSchema.nullable(),
    photoPath: directoryWebPhotoPathSchema.nullable(),
    isSelf: z.boolean(),
})
    .strict();
/** `GET /api/web/directory/me`. `person: null` = this login is not linked to a directory person. */
export const directoryMeDTOSchema = z
    .object({ person: directoryPersonSummaryDTOSchema.nullable() })
    .strict();
/** Most direct reports a profile or org chart lists; `directReportCount` is always the true total. */
export const DIRECTORY_REPORTS_MAX = 200;
/** A location as a profile shows it: the IANA zone the browser renders local time from. */
export const directoryLocationDTOSchema = z
    .object({ id: z.string().min(1), name: z.string().min(1), timezone: z.string().min(1) })
    .strict();
/**
 * `GET /api/web/directory/people/:id`. AN ALLOW-LIST (docs/008 S7). CONTACT FIELDS (adr/0036
 * addendum 2026-10-18): a directory member sees a colleague's work `email` and `phone` HERE and
 * nowhere else - never on the grid card, the org chart or facets. Never `userId`, `externalId`,
 * `employmentStatus`, storage keys. Related people are summaries (no contact fields) and pass the
 * same visibility rule as the grid. Pronouns and bio (adr/0036 addendum 'Profile fields and the import') are the person's own plain-text words, shown here only.
 */
export const directoryProfileDTOSchema = z
    .object({
    id: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    title: z.string().nullable(),
    personType: directoryPersonTypeSchema,
    isSelf: z.boolean(),
    department: directoryRefDTOSchema.nullable(),
    photoPath: directoryWebPhotoPathSchema.nullable(),
    /** The person's own words (S47-rsq5 Phase 5): plain text, profile-only - never on a card, org node or `/directory/me`. */
    pronouns: z.string().min(1).nullable(),
    bio: z.string().min(1).nullable(),
    /** Which external directory owns this person - null = added by hand (S49-3u50). */
    directorySource: directoryProviderSchema.nullable(),
    email: z.string().min(1).nullable(),
    phone: z.string().min(1).nullable(),
    primaryLocation: directoryLocationDTOSchema.nullable(),
    secondaryLocations: z.array(directoryLocationDTOSchema),
    manager: directoryPersonSummaryDTOSchema.nullable(),
    directReports: z.array(directoryPersonSummaryDTOSchema).max(DIRECTORY_REPORTS_MAX),
    directReportCount: z.number().int().min(0),
    assistants: z.array(directoryPersonSummaryDTOSchema),
    assistantOf: z.array(directoryPersonSummaryDTOSchema),
})
    .strict();
/** `:id` on every `/api/web/directory/people/:id...` route (S4). */
export const directoryPersonIdParamsSchema = z
    .object({ id: z.string().trim().min(1).max(64) })
    .strict();
/** `:id` and `:fileId` on `GET /api/web/directory/people/:id/photo/:fileId` (S4; S47-rsq5-TASK-058). */
export const directoryPhotoParamsSchema = z
    .object({ id: z.string().trim().min(1).max(64), fileId: z.string().trim().min(1).max(64) })
    .strict();
/** Most managers an org chart walks up. Real organisations are under 15 deep; the cap only
 *  bounds bad data. `chainTruncated` says the top was not reached. */
export const DIRECTORY_ORG_CHAIN_MAX = 50;
/** One node of the org chart: a card summary plus how many visible direct reports it has. */
export const directoryOrgNodeDTOSchema = directoryPersonSummaryDTOSchema
    .extend({ directReportCount: z.number().int().min(0) })
    .strict();
/** `GET /api/web/directory/people/:id/org`. `managerChain[0]` is the direct manager; the last is the top. */
export const directoryOrgChartDTOSchema = z
    .object({
    person: directoryOrgNodeDTOSchema,
    managerChain: z.array(directoryOrgNodeDTOSchema).max(DIRECTORY_ORG_CHAIN_MAX),
    chainTruncated: z.boolean(),
    directReports: z.array(directoryOrgNodeDTOSchema).max(DIRECTORY_REPORTS_MAX),
})
    .strict();
/** What a directory admin can do for one person (S47-rsq5 Phase 3). */
export const DIRECTORY_ACCESS_STATUSES = [
    'has_access',
    'invited',
    'can_invite',
    'can_grant',
    'unavailable',
];
export const directoryAccessStatusSchema = z.enum(DIRECTORY_ACCESS_STATUSES);
export const DIRECTORY_ACCESS_UNAVAILABLE_REASONS = [
    'no_email',
    'email_in_use',
    'email_ambiguous',
    'revoked',
];
export const directoryAccessUnavailableReasonSchema = z.enum(DIRECTORY_ACCESS_UNAVAILABLE_REASONS);
/** `GET|POST /api/web/directory/people/:id/access`. Status only (S7): never a user id, email or membership. `reason` is non-null exactly when `status` is `unavailable`. */
export const directoryPersonAccessDTOSchema = z
    .object({
    status: directoryAccessStatusSchema,
    reason: directoryAccessUnavailableReasonSchema.nullable(),
})
    .strict()
    .refine((v) => (v.status === 'unavailable') === (v.reason !== null), {
    message: 'REASON_MISMATCH',
});
/**
 * `POST /api/web/directory/people/:id/access` body. No field: the person is the path id and the role
 * is fixed (`directory.member`); `.strict()` refuses a smuggled roleKey/tenantId (S1, no-escalation).
 */
export const grantDirectoryAccessRequestSchema = z.object({}).strict();
/** A grid page: 48 fills 2, 3, 4, 6 and 8 columns evenly. */
export const DIRECTORY_PAGE_SIZE = 48;
/** Longest Directory search the API accepts (S4). Longer than any name + title anyone types. */
export const DIRECTORY_SEARCH_MAX_LENGTH = 100;
/** Below this many characters a search matches name PREFIXES only — trigrams of one or two
 *  letters match nearly everyone. From this length on, typo-tolerant matching joins in. */
export const DIRECTORY_SEARCH_FUZZY_MIN_LENGTH = 3;
const directoryFilterIdSchema = z.string().trim().min(1).max(64);
/** The filter half of a Directory read — the grid and the facets. */
export const directoryPeopleFilterSchema = z.object({
    q: z.string().trim().max(DIRECTORY_SEARCH_MAX_LENGTH).optional(),
    locationId: directoryFilterIdSchema.optional(),
    departmentId: directoryFilterIdSchema.optional(),
});
export const directoryPeopleQuerySchema = pageQuerySchema
    .extend({
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DIRECTORY_PAGE_SIZE),
})
    .merge(directoryPeopleFilterSchema);
/** Most chips a facet row returns, highest count first. A tenant with more locations or
 *  departments than this still filters correctly by URL; the row simply stops offering more. */
export const DIRECTORY_FACET_MAX = 100;
/** A chip: a location or department, and how many directory-visible people it holds under the
 *  OTHER active filters (disjunctive). `count: 0` only for a selected id echoed back. */
export const directoryFacetDTOSchema = z
    .object({ id: z.string().min(1), name: z.string().min(1), count: z.number().int().min(0) })
    .strict();
export const directoryFacetsDTOSchema = z
    .object({
    locations: z.array(directoryFacetDTOSchema),
    departments: z.array(directoryFacetDTOSchema),
})
    .strict();
/** `GET /api/web/directory/facets` — the same filter the grid takes, no paging. */
export const directoryFacetsQuerySchema = directoryPeopleFilterSchema;
/** `GET|PATCH /api/web/directory/settings` — the caller's own tenant's Directory switches
 *  (adr/0036 decision 6). No tenantId on the wire (S1): the server reads it from the token. */
export const directorySettingsDTOSchema = z.object({ showContractors: z.boolean() }).strict();
/** One switch today, so it is required; `.strict()` refuses any undeclared key (a smuggled tenantId included). */
export const updateDirectorySettingsSchema = z.object({ showContractors: z.boolean() }).strict();
/** Longest pronouns a person may set ("she/her", "they/them", "il/lui · he/him"). Code points. */
export const DIRECTORY_PRONOUNS_MAX = 40;
/** Longest bio. Code points. A paragraph or two — the profile is a card, not a CV. */
export const DIRECTORY_BIO_MAX = 500;
/**
 * PLAIN TEXT, NO MARKUP (S47-rsq5 Phase 5). `label` / `freeText` refuse `<` and `>` (text-policy C7), so
 * no HTML can be stored; the SPA renders the value as a React text node regardless (validation is not
 * the XSS control — React's escaping is). Shared by the self-edit route, the admin person routes and the
 * employee import, so the three cannot disagree.
 */
export const directoryPronounsSchema = label({ max: DIRECTORY_PRONOUNS_MAX });
export const directoryBioSchema = freeText({ max: DIRECTORY_BIO_MAX, multiLine: true });
/**
 * What an employee may change about THEMSELVES (`PATCH /api/web/directory/me`). Every key optional; an
 * explicit `null` clears the value. `.strict()`: any other key — `title`, `email`, `departmentId`,
 * `firstName`, a smuggled `personId` or `tenantId` — is 400 (S4; the person comes from the token, S1).
 * `phone` is E.164, the shape `PhoneInput` emits (the import may store other phone text; see adr/0036).
 */
export const myDirectoryDetailsFieldsSchema = z
    .object({
    phone: e164Schema.nullable().optional(),
    pronouns: directoryPronounsSchema.nullable().optional(),
    bio: directoryBioSchema.nullable().optional(),
})
    .strict();
/** The route body: the fields schema plus "at least one key with a value" (`undefined` does not count). */
export const updateMyDirectoryDetailsSchema = myDirectoryDetailsFieldsSchema.refine((value) => Object.values(value).some((v) => v !== undefined), { message: 'EMPTY_UPDATE' });
/** The route's answer: the three stored values after normalisation (S7 allow-list — nothing else). */
export const myDirectoryDetailsDTOSchema = z
    .object({ phone: z.string().nullable(), pronouns: z.string().nullable(), bio: z.string().nullable() })
    .strict();
/**
 * Most invitations one `POST /api/web/directory/invite-everyone` sends (S47-rsq5 Phase 5; adr/0036 addendum
 * "Invite everyone"). One invitation provisions a login and sends an email (~0.5 s); 20 keeps a request far
 * inside the API's 25 s Lambda timeout. A client repeats the request while `remaining > 0`.
 */
export const DIRECTORY_INVITE_EVERYONE_BATCH_MAX = 20;
/** `GET /api/web/directory/invite-everyone` — how many employees one action would invite. */
export const directoryInviteEveryonePreviewDTOSchema = z
    .object({ toInvite: z.number().int().min(0) })
    .strict();
/** `POST` body: nothing to choose — who is decided by the server's rule, the role is fixed (`directory.member`). */
export const inviteEveryoneRequestSchema = z.object({}).strict();
/** One batch's outcome. `remaining` is a fresh count after the batch — 0 means done. */
export const directoryInviteEveryoneResultDTOSchema = z
    .object({
    invited: z.number().int().min(0),
    skipped: z.number().int().min(0),
    remaining: z.number().int().min(0),
})
    .strict();
//# sourceMappingURL=employee-directory.js.map